import type { FC } from 'react'
import { useMemo, useState } from 'react'

import { Input, LoaderCircle, Radio, RadioGroup, Select, Textarea } from '@skytech/ui'
import type { ValueLabelPair } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { LOCALE_WITH_NAMES } from '@skytech/utils/locale'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import * as yup from 'yup'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'

import queries from '../queries'
import adminResources from '../resources'
import { typeOptions } from '../resources/assets/constants'

type Values = {
  id?: number
  code: string
  locale: string
  type: string
  subject: string
  text: string
  description: string
}

type MutateParams = {
  body: Values
}

type Props = {
  textTemplateId: number
  codeOptions: ValueLabelPair[]
  onClose: () => void
}

export const TextTemplateForm: FC<Props> = ({ onClose, codeOptions, textTemplateId }) => {
  if (isNaN(textTemplateId)) {
    throw new Error('Text template ID must be a number')
  }

  const translate = useTranslation()
  const queryClient = useQueryClient()
  const [textTemplateDetails, setTextTemplateDetails] = useState<Values>()

  const translatedMandatoryMessage = translate('This field is mandatory')

  const validationSchema = yup.object({
    code: yup.string().required(translatedMandatoryMessage),
    type: yup.string().required(translatedMandatoryMessage),
    text: yup.string().required(translatedMandatoryMessage),
    subject: yup.string().required(translatedMandatoryMessage),
    description: yup.string().required(translatedMandatoryMessage),
  })

  const { isFetching } = useQuery(
    queries.adminTextTemplateDetails(textTemplateId),
    () => adminResources.getTextTemplateDetails(textTemplateId),
    {
      onSuccess(data) {
        setTextTemplateDetails(data)
      },
    }
  )

  // update or insert
  const upsert = (body: Values) => {
    if (textTemplateDetails?.id && textTemplateId) {
      return adminResources.updateTextTemplate(body, textTemplateId)
    } else {
      return adminResources.postTextTemplate(body)
    }
  }

  const getDefaultValues = async (code: string) => {
    const defaultValues = await adminResources.getTextTemplateDefaultValues(code)
    if (defaultValues) {
      return setTextTemplateDetails({ ...defaultValues })
    }
  }

  const { mutate, isLoading } = useMutation(({ body }: MutateParams) => upsert(body), {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminTextTemplates())
      onClose()
    },
  })

  const localeOptions = useMemo(
    () => Object.entries(LOCALE_WITH_NAMES).map(([value, label]) => ({ value, label })),
    []
  )

  // when refetching data
  if (isFetching) {
    return <LoaderCircle />
  }

  return (
    <div className="customer-address-new">
      <h2>
        <Translation id="Add text template" />
      </h2>
      <Formik
        initialValues={{
          code: textTemplateDetails?.code || '',
          type: textTemplateDetails?.type || '',
          locale: textTemplateDetails?.locale || '',
          subject: textTemplateDetails?.subject || '',
          text: textTemplateDetails?.text || '',
          description: textTemplateDetails?.description || '',
        }}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={formValues => {
          mutate({ body: formValues })
        }}
      >
        <Form>
          <Field name="code">
            {({ field, meta, form }: FieldProps) => (
              <div className="flex items-center">
                <Select
                  capitalizeLabel
                  id="code"
                  className="w-full"
                  label={
                    <>
                      <Translation id="Code" />*
                    </>
                  }
                  options={codeOptions}
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                  onChange={option => form.setFieldValue(field.name, option?.value)}
                />
                {!textTemplateDetails?.id && (
                  <Button
                    size="small"
                    onClick={() => getDefaultValues(field.value)}
                    disabled={!field.value}
                  >
                    Default Values
                  </Button>
                )}
              </div>
            )}
          </Field>
          <Field name="type">
            {({ field, meta, form }: FieldProps) => (
              <Select
                capitalizeLabel
                id="type"
                label={
                  <>
                    <Translation id="Type" />*
                  </>
                }
                options={typeOptions}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
                onChange={option => form.setFieldValue(field.name, option?.value)}
              />
            )}
          </Field>
          <Field name="locale">
            {({ field, meta, form }: FieldProps) => (
              <Select
                capitalizeLabel
                id="locale"
                label={
                  <>
                    <Translation id="Locale" />*
                  </>
                }
                options={localeOptions}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
                onChange={option => form.setFieldValue(field.name, option?.value)}
              />
            )}
          </Field>
          <Field name="subject">
            {({ field, meta }: FieldProps) => (
              <Input
                isFullWidth
                capitalizeLabel
                id="subject"
                label={
                  <>
                    <Translation id="Subject" />*
                  </>
                }
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>
          <Field name="text">
            {({ field, meta }: FieldProps) => (
              <Textarea
                isFullWidth
                id="text"
                capitalizeLabel
                label={`${translate('Text')}*`}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>
          <Field name="description">
            {({ field, meta }: FieldProps) => (
              <Textarea
                isFullWidth
                id="description"
                capitalizeLabel
                label={<Translation id="Description" />}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>
          <RadioGroup
            id="address-type"
            label={
              <>
                <Translation id="RadioType" />*
              </>
            }
          >
            <Field name="isGlobal">
              {({ field }: FieldProps) => (
                <Radio
                  capitalizeLabel
                  id="global"
                  label={<Translation id="Global" />}
                  marginBottom={16}
                  {...field}
                />
              )}
            </Field>

            <Field name="isGlobal">
              {({ field }: FieldProps) => (
                <Radio
                  capitalizeLabel
                  id="local"
                  label={<Translation id="Local" />}
                  marginBottom={16}
                  {...field}
                />
              )}
            </Field>
          </RadioGroup>
          <footer>
            <Button variant="tertiary" disabled={isLoading} onClick={onClose}>
              <Translation id="Cancel" />
            </Button>

            <Button type="submit" isLoading={isLoading}>
              <Translation id="Submit" />
            </Button>
          </footer>
        </Form>
      </Formik>
    </div>
  )
}
